import React, { useState, useEffect, forwardRef, useImperativeHandle, useRef } from 'react';
import { FileText, BarChart3, TrendingUp, PieChart, BarChart, LineChart, Clock } from 'lucide-react';
import { useOrganization, useClerk } from '@clerk/clerk-react';
import ReportExecutionModal from './ReportExecutionModal';
import ReportGenerationProgressModal from './ReportGenerationProgressModal';
import { getApiBaseUrl, getOrgSlug } from '../config/backend.config';
import './SavedReportsPanel.css';

const SavedReportsPanel = forwardRef(({ savedReports, setSavedReports, onLoadReport, user, selectedWorkspace, onSaveReport, onOpenReportEditor, reportProgressEvents }, ref) => {
  const { organization } = useOrganization();
  const clerk = useClerk();
  const [inProgressReports, setInProgressReports] = useState([]);
  const [executionModal, setExecutionModal] = useState({ isOpen: false, result: null, reportName: '', reportId: null });
  const [generationModal, setGenerationModal] = useState({ isOpen: false, jobId: null, reportName: '' });
  const [, setRunningReports] = useState(new Set());
  const [, setExecutionResults] = useState({});
  const [savedReportsLoading, setSavedReportsLoading] = useState(false);
  const [activeJobIds, setActiveJobIds] = useState(new Set()); // Track jobs being polled
  const [, setCompletedJobIds] = useState(new Set()); // Track jobs already processed for completion
  const [newlyCompletedReportNames, setNewlyCompletedReportNames] = useState(new Set()); // Track names for animation

  // Expose handleSaveReportStarted via ref for parent component
  useImperativeHandle(ref, () => ({
    onSaveReportStarted: handleSaveReportStarted
  }));

  // Clean up in-progress reports when component unmounts
  useEffect(() => {
    return () => {
      setInProgressReports([]);
      setActiveJobIds(new Set());
      setCompletedJobIds(new Set());
    };
  }, []);

  // Watch WebSocket events for job completion (replacing polling)
  // Use ref to track which jobs we've already processed for completion
  const processedCompletionsRef = useRef(new Set());

  useEffect(() => {
    if (!reportProgressEvents || activeJobIds.size === 0) {
      return;
    }

    // Check each active job for completion via WebSocket events
    const newlyCompletedJobs = [];

    for (const jobId of activeJobIds) {
      // Skip if already processed (use ref to persist across renders)
      if (processedCompletionsRef.current.has(jobId)) {
        continue;
      }

      const progress = reportProgressEvents[jobId];
      if (progress && (progress.status === 'complete' || progress.status === 'cancelled')) {
        // Mark as processed IMMEDIATELY in ref to prevent re-processing
        processedCompletionsRef.current.add(jobId);
        newlyCompletedJobs.push(jobId);
      }
    }

    if (newlyCompletedJobs.length > 0) {
      // Track report names for animation (only successful completions)
      const completedReportNames = new Set();
      newlyCompletedJobs.forEach(jobId => {
        const progress = reportProgressEvents[jobId];
        if (progress?.success && progress?.reportName) {
          completedReportNames.add(progress.reportName);
        }
      });
      if (completedReportNames.size > 0) {
        setNewlyCompletedReportNames(completedReportNames);
        // Clear animation state after animation completes
        setTimeout(() => setNewlyCompletedReportNames(new Set()), 2000);
      }

      // Update state to reflect completion
      setCompletedJobIds(prev => {
        const newSet = new Set(prev);
        newlyCompletedJobs.forEach(id => newSet.add(id));
        return newSet;
      });

      // Remove completed jobs from active jobs
      setActiveJobIds(prev => {
        const newSet = new Set(prev);
        newlyCompletedJobs.forEach(id => newSet.delete(id));
        return newSet;
      });

      // Remove from in-progress list
      setInProgressReports(prev => prev.filter(r => !newlyCompletedJobs.includes(r.jobId)));

      // Refresh saved reports to show the new report (only once per completion)
      loadSavedReports();
    }
  }, [reportProgressEvents, activeJobIds]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load saved reports from backend API on component mount and when workspace changes
  useEffect(() => {
    if (organization && clerk?.session && selectedWorkspace?.workspace_id) {
      // Clear in-progress reports and active jobs when switching workspaces
      setInProgressReports([]);
      setActiveJobIds(new Set());
      setCompletedJobIds(new Set());
      processedCompletionsRef.current = new Set(); // Reset the ref too
      // Close generation modal when workspace changes
      setGenerationModal({ isOpen: false, jobId: null, reportName: '' });

      loadSavedReports();
    }
  }, [selectedWorkspace?.workspace_id, organization?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadSavedReports = async () => {
    if (!organization || !clerk?.session) return;

    // Validate selected workspace
    if (!selectedWorkspace?.workspace_id) {
      console.error('No workspace selected');
      return;
    }

    setSavedReportsLoading(true);
    try {
      // Get Clerk session token for authentication
      const sessionToken = await clerk.session.getToken();
      if (!sessionToken) {
        console.error('Unable to get authentication token');
        return;
      }

      // Get organization-specific API URL
      const orgSlug = getOrgSlug(organization);
      const apiBaseUrl = getApiBaseUrl(orgSlug);

      // Add workspace_id query parameter (required by API)
      const queryParams = new URLSearchParams({
        workspace_id: selectedWorkspace.workspace_id
      });

      const response = await fetch(`${apiBaseUrl}/saved-reports/?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`Failed to load saved reports: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        // Convert backend format to frontend format
        // Filter out pending reports (shown in in-progress section instead)
        const convertedReports = data.reports
          .filter(report => report.status !== 'pending')
          .map(report => ({
            id: report.report_id,
            name: report.name,
            description: report.description,
            createdAt: report.created_at,
            template: { name: 'Saved Report' }, // Default template name
            messages: [], // Backend reports don't have messages
            schedule: report.schedule || null,
            status: report.status,
            runCount: report.run_count || 0,
            lastRun: report.last_run
          }));

        setSavedReports(convertedReports);
      } else {
        console.error('Failed to load saved reports:', data.detail);
      }
    } catch (error) {
      console.error('Error loading saved reports:', error);
    } finally {
      setSavedReportsLoading(false);
    }
  };

  const handleSaveReportStarted = (saveData) => {
    // Open generation progress modal when save is started
    // saveData = { jobId, reportName }
    setGenerationModal({
      isOpen: true,
      jobId: saveData.jobId,
      reportName: saveData.reportName
    });

    // Remove any existing in-progress reports first (in case of multiple saves)
    setInProgressReports(prev => prev.filter(r => r.id !== `temp_${saveData.jobId}`));

    // Add a temporary report to the in-progress list showing "Generating..."
    const tempReport = {
      id: `temp_${saveData.jobId}`,
      name: saveData.reportName,
      description: '',
      createdAt: new Date().toISOString(),
      template: { name: 'Saved Report' },
      messages: [],
      status: 'generating',
      runCount: 0,
      lastRun: null,
      jobId: saveData.jobId  // Store job ID for cleanup later
    };

    setInProgressReports(prev => [tempReport, ...prev]);

    // Add to active jobs for background polling (ensures update even if modal is closed)
    setActiveJobIds(prev => new Set([...prev, saveData.jobId]));
  };

  const handleGenerationComplete = () => {
    // Simply close the modal - left panel status remains unchanged
    // This lets user close the modal while generation continues in background
    setGenerationModal({ isOpen: false, jobId: null, reportName: '' });
  };

  const handleGenerationActuallyComplete = (jobId) => {
    // Called when generation actually finishes (not just modal close)
    // Only remove from in-progress list - the useEffect watching reportProgressEvents
    // will handle refreshing saved reports to avoid duplicate API calls
    setInProgressReports(prev => prev.filter(r => r.jobId !== jobId));
    setActiveJobIds(prev => {
      const newSet = new Set(prev);
      newSet.delete(jobId);
      return newSet;
    });
    // Note: Don't call loadSavedReports() here - it's called by the useEffect
    // watching reportProgressEvents to avoid duplicate API calls
  };

  const handleOpenReportModal = (reportId) => {
    // Open modal to view report details, run history, and execute
    setExecutionModal({
      isOpen: true,
      result: null,
      reportName: savedReports.find(r => r.id === reportId)?.name || 'Unknown Report',
      reportId: reportId
    });
  };

  const handleDeleteReport = async (reportId) => {
    if (!organization || !clerk?.session) return;

    try {
      // Get Clerk session token for authentication
      const sessionToken = await clerk.session.getToken();
      if (!sessionToken) {
        console.error('Unable to get authentication token');
        return;
      }

      // Get organization-specific API URL
      const orgSlug = getOrgSlug(organization);
      const apiBaseUrl = getApiBaseUrl(orgSlug);

      // Add workspace_id query parameter (required by API)
      const queryParams = new URLSearchParams({
        workspace_id: selectedWorkspace.workspace_id
      });

      const response = await fetch(`${apiBaseUrl}/saved-reports/${reportId}?${queryParams}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to delete report: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.success) {
        // Remove report from local state
        setSavedReports(prev => prev.filter(report => report.id !== reportId));
        // Close the execution modal
        setExecutionModal({ isOpen: false, result: null, reportName: '', reportId: null });
      } else {
        console.error('Failed to delete report:', data.detail);
      }
    } catch (error) {
      console.error('Error deleting report:', error);
    }
  };

  const handleRunReport = async (reportId) => {
    if (!organization || !clerk?.session) return;

    // Validate selected workspace
    if (!selectedWorkspace?.workspace_id) {
      console.error('No workspace selected');
      return;
    }

    setRunningReports(prev => new Set([...prev, reportId]));

    try {
      // Get Clerk session token for authentication
      const sessionToken = await clerk.session.getToken();
      if (!sessionToken) {
        console.error('Unable to get authentication token');
        return;
      }

      // Get organization-specific API URL
      const orgSlug = getOrgSlug(organization);
      const apiBaseUrl = getApiBaseUrl(orgSlug);

      // Use query parameters with workspace ID
      const queryParams = new URLSearchParams({
        workspace_id: selectedWorkspace.workspace_id
      });

      const response = await fetch(`${apiBaseUrl}/saved-reports/${reportId}/run?${queryParams}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json',
        }
      });
      
      const data = await response.json();
      const report = savedReports.find(r => r.id === reportId);

      // Check if the overall response was successful AND the execution was successful
      if (data.success && data.execution_result) {
        const executionResult = data.execution_result;

        setExecutionResults(prev => ({
          ...prev,
          [reportId]: executionResult
        }));

        // Open execution modal with results
        setExecutionModal({
          isOpen: true,
          result: executionResult,
          reportName: report?.name || 'Unknown Report',
          reportId: reportId
        });

        // Show success or failure message based on execution result
        if (executionResult.success) {
          console.log('Report executed successfully:', executionResult);
        } else {
          console.error('Report execution failed:', executionResult.stderr);
        }
      } else {
        // Handle API-level failure
        console.error('Failed to run report:', data.detail || data.message);

        // Show error in execution modal
        setExecutionModal({
          isOpen: true,
          result: {
            success: false,
            return_code: -1,
            stdout: '',
            stderr: data.detail || data.message || 'Unknown error',
            execution_time_seconds: 0,
            outputs: [],
            error_message: data.detail || data.message || 'Unknown error'
          },
          reportName: report?.name || 'Unknown Report',
          reportId: reportId
        });
      }
    } catch (error) {
      console.error('Error running report:', error);
      
      // Show error in execution modal
      const report = savedReports.find(r => r.id === reportId);
      setExecutionModal({
        isOpen: true,
        result: {
          success: false,
          return_code: -1,
          stdout: '',
          stderr: error.message,
          execution_time_seconds: 0,
          outputs: [],
          error_message: error.message
        },
        reportName: report?.name || 'Unknown Report',
        reportId: reportId
      });
    } finally {
      setRunningReports(prev => {
        const newSet = new Set(prev);
        newSet.delete(reportId);
        return newSet;
      });
    }
  };

  // Suppress unused variable warnings for user and onSaveReport (kept for API compatibility)
  void user;
  void onSaveReport;
  void onLoadReport;

  const handleCloseExecutionModal = () => {
    setExecutionModal({ isOpen: false, result: null, reportName: '', reportId: null });
  };

  const handleOpenEditorModal = (reportId, reportName) => {
    // Close execution modal when opening editor
    setExecutionModal({ isOpen: false, result: null, reportName: '', reportId: null });
    // Navigate to full-page editor
    if (onOpenReportEditor) {
      onOpenReportEditor(reportId, reportName);
    }
  };

  // Get icon based on report name pattern
  const getReportIcon = (reportName) => {
    const name = reportName?.toLowerCase() || '';
    if (name.includes('trend') || name.includes('growth')) return <TrendingUp size={16} />;
    if (name.includes('pie') || name.includes('distribution')) return <PieChart size={16} />;
    if (name.includes('bar') || name.includes('comparison')) return <BarChart size={16} />;
    if (name.includes('line') || name.includes('progress') || name.includes('timeline')) return <LineChart size={16} />;
    return <BarChart3 size={16} />;
  };

  // Format date to "21 Dec" format
  const formatDate = (dateString) => {
    if (!dateString) return 'Never run';
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    return `${day} ${month}`;
  };

  return (
    <div className="template-panel">
      {/* Saved Reports Section */}
      <div className="saved-reports-section">
        <div className="saved-reports-header">
          <div className="saved-reports-title">
            <span>REPORTS</span>
          </div>
        </div>

        {/* In Progress Reports - shown below header but not in the scrollable list */}
        {inProgressReports.length > 0 && (
          <div className="in-progress-reports-container">
            {inProgressReports.map((report) => (
              <div key={report.id} className="in-progress-report-item">
                <div
                  className="in-progress-report-content"
                  onClick={() => {
                    // Open generation modal for in-progress reports
                    setGenerationModal({
                      isOpen: true,
                      jobId: report.jobId,
                      reportName: report.name
                    });
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="in-progress-report-icon-name">
                    <div className="in-progress-report-icon">
                      {getReportIcon(report.name)}
                    </div>
                    <span className="in-progress-report-name">{report.name}</span>
                  </div>
                  <span className="in-progress-report-generating">
                    <span className="generating-spinner"><Clock size={14} /></span>
                    Generating...
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="saved-reports-list">
          {savedReportsLoading ? (
            <div className="saved-reports-loading">
              <div className="loading-spinner"></div>
              <span>Loading saved reports...</span>
            </div>
          ) : savedReports.length === 0 ? (
            <div className="saved-reports-empty">
              <FileText size={16} />
              <p>No saved reports</p>
              <span>Save conversations to see them here</span>
            </div>
          ) : (
            savedReports.map((report) => (
              <div key={report.id} className={`saved-report-item ${newlyCompletedReportNames.has(report.name) ? 'newly-completed' : ''}`}>
                <div
                  className="saved-report-content"
                  onClick={() => handleOpenReportModal(report.id)}
                >
                  <div className="saved-report-header">
                    <div className="saved-report-icon-name">
                      <div className="saved-report-icon">
                        {getReportIcon(report.name)}
                      </div>
                      <span className="saved-report-name">{report.name}</span>
                    </div>
                  </div>

                  <div className="saved-report-meta">
                    <div className="saved-report-details">
                      <span className="saved-report-last-run">
                        {formatDate(report.lastRun)}
                      </span>
                      <span className="saved-report-separator">·</span>
                      <span className="saved-report-runs">
                        {report.runCount || 0} runs
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Report Execution Modal */}
      <ReportExecutionModal
        isOpen={executionModal.isOpen}
        onClose={handleCloseExecutionModal}
        executionResult={executionModal.result}
        reportName={executionModal.reportName}
        reportId={executionModal.reportId}
        selectedWorkspace={selectedWorkspace}
        onRun={() => handleRunReport(executionModal.reportId)}
        onEdit={() => handleOpenEditorModal(executionModal.reportId, executionModal.reportName)}
        onDelete={() => handleDeleteReport(executionModal.reportId)}
      />

      {/* Report Generation Progress Modal */}
      <ReportGenerationProgressModal
        isOpen={generationModal.isOpen}
        jobId={generationModal.jobId}
        reportName={generationModal.reportName}
        selectedWorkspace={selectedWorkspace}
        onClose={handleGenerationComplete}
        onComplete={handleGenerationActuallyComplete}
        progressData={reportProgressEvents?.[generationModal.jobId]}
      />

          </div>
  );
});

SavedReportsPanel.displayName = 'SavedReportsPanel';

export default SavedReportsPanel;
